import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntriesStore } from '../../stores';
import { EntryPresentation } from './EntryPresentation';

export function EntryList() {
  const { t } = useTranslation();
  const {
    entries,
    pagination,
    hasMore,
    isLoading,
    isFetchingMore,
    error,
    filters,
    fetchEntries,
  } = useEntriesStore();

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries, filters]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries[0].isIntersecting && hasMore && !isFetchingMore && pagination) {
          fetchEntries(pagination.page + 1);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, pagination, fetchEntries]);

  if (isLoading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-800">
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    const hasSearchFilter = !!filters.search?.trim();

    return (
      <div className="py-12 text-center">
        <svg
          className="mx-auto h-12 w-12 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {hasSearchFilter ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          )}
        </svg>
        <h3 className="mt-2 text-sm font-medium text-slate-900">
          {hasSearchFilter ? t('entries.noEntriesFound') : t('entries.noEntries')}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          {hasSearchFilter ? t('entries.tryDifferentSearch') : t('entries.startWriting')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        {entries.map((entry) => (
          <EntryPresentation key={entry.id} entry={entry} />
        ))}
      </div>

      <div ref={sentinelRef} />

      {isFetchingMore && (
        <div className="flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      )}
    </div>
  );
}
